import { lazy, Suspense, useEffect, useState } from 'react';
import { ArrowRight, Bold, Code2, Italic, List, Underline } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { MessageContent } from '@/components/ui/MessageContent';
import { ObjectActivity } from '@/components/ui/ObjectActivity';
import { Button } from '@/components/ui/Button';
import { richMessage } from '@/lib/richText';
import { COMMENTS_HOME, INITIAL_ACTIVITY, PEOPLE } from '../comments/model';
import { Section, Code } from './Section';
const Editor = lazy(() =>
  import('@/components/ui/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);
const seed = richMessage({
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [
        { type: 'text', text: 'Текст в одном стиле. ' },
        { type: 'text', text: 'Важное', marks: [{ type: 'bold' }, { type: 'underline' }] },
        { type: 'text', text: ' можно выделить, а код — проверить.' },
      ],
    },
    {
      type: 'codeBlock',
      attrs: { language: 'typescript' },
      content: [{ type: 'text', text: 'const ready = items.every(item => item.done);' }],
    },
  ],
});
export function CommentPatternsSection() {
  const { hash } = useLocation(),
    [tryEditor, setTryEditor] = useState(false),
    [value, setValue] = useState(seed);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (hash !== '#comment-patterns') return;
    let active = true;
    const align = () => {
      if (active)
        document.getElementById('comment-patterns')?.scrollIntoView({ block: 'start', behavior: 'instant' });
    };
    const frame = requestAnimationFrame(align);
    void document.fonts.ready.then(align);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
    };
  }, [hash]);
  return (
    <Section
      id="comment-patterns"
      title="Комментарии и редактор"
      lead="Обсуждение и история любого объекта: общие сообщения, безопасный текст, вложения и один редактор в стиле Kaname."
    >
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-line p-5">
          <div>
            <h3 className="type-heading">Обсуждение и изменения</h3>
            <p className="mt-1 text-sm text-fg-muted">Автор, ветки и что изменилось: было → стало.</p>
          </div>
          <Link
            to={COMMENTS_HOME}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Открыть образец
            <ArrowRight className="size-4" />
          </Link>
        </header>
        <div className="max-h-96 overflow-auto">
          <ObjectActivity
            entries={INITIAL_ACTIVITY.slice(0, 2)}
            entities={PEOPLE}
            expanded={expanded}
            onExpandedChange={(id, open) =>
              setExpanded((old) => {
                const next = new Set(old);
                if (open) next.add(id);
                else next.delete(id);
                return next;
              })
            }
          />
        </div>
        <div className="border-t border-line p-4">
          <Code>ObjectActivity · Timeline · MessageThread · MessageContent</Code>
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-line p-5">
          <div>
            <h3 className="type-heading">Встроенный редактор</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Форматирование, списки, ссылки, код, смайлики и упоминания.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setTryEditor(true)} disabled={tryEditor}>
            Попробовать редактор
          </Button>
        </header>
        {tryEditor ? (
          <Suspense
            fallback={
              <div className="min-h-48 bg-muted p-4 text-sm text-fg-muted" role="status">
                Открываем редактор…
              </div>
            }
          >
            <Editor value={value} onChange={setValue} entities={PEOPLE} label="Редактор в галерее" />
          </Suspense>
        ) : (
          <div>
            <div
              aria-hidden
              className="flex items-center gap-4 border-b border-line bg-muted px-5 py-3 text-fg-muted"
            >
              <Bold className="size-4" />
              <Italic className="size-4" />
              <Underline className="size-4" />
              <List className="size-4" />
              <Code2 className="size-4" />
            </div>
            <div className="p-5">
              <MessageContent content={seed} />
            </div>
          </div>
        )}
        <div className="border-t border-line p-4">
          <Code>RichTextEditor · CommentComposer · Undo / Redo</Code>
        </div>
      </div>
    </Section>
  );
}
